import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Users, LayoutGrid, LogOut, Sparkles, UsersRound, ShieldCheck, Puzzle, Layers, CreditCard, Settings, Ticket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { NAV, AUTH } from "@/constants/testIds";
import TrialCountdownBadge from "@/components/TrialCountdownBadge";
import BetaTrialBanner from "@/components/BetaTrialBanner";
import MilestoneBanner from "@/components/MilestoneBanner";
import FeedbackWidget from "@/components/FeedbackWidget";
import OnboardingTour from "@/components/OnboardingTour";

const linkBase = "flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors";
const linkClass = ({ isActive }) =>
  `${linkBase} ${isActive ? "bg-slate-900 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`;
const mobileLinkClass = ({ isActive }) =>
  `flex-1 flex flex-col items-center justify-center gap-1 py-2.5 text-[11px] font-semibold transition-colors ${
    isActive ? "text-slate-900" : "text-slate-400"
  }`;

export default function AppShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const handleLogout = async () => { await logout(); nav("/auth"); };
  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-50 relative z-10">
      <header className="md:hidden sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img src="/icon-192.png" alt="Discrimina" className="w-8 h-8 rounded-lg" />
          <div className="font-display font-extrabold text-base">Discrimina</div>
        </div>
        <div className="flex items-center gap-3">
          <TrialCountdownBadge />
          <button onClick={handleLogout} data-testid={AUTH.logout + "-mobile"}
            className="text-slate-500 hover:text-slate-800" aria-label="Log out">
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      <aside data-testid={NAV.sidebar}
        className="hidden md:flex w-64 shrink-0 border-r border-slate-200 bg-white/70 backdrop-blur-sm flex-col">
        <div className="px-6 pt-8 pb-6">
          <div className="flex items-center gap-2">
            <img src="/icon-192.png" alt="Discrimina" className="w-9 h-9 rounded-xl" />
            <div>
              <div className="font-display text-lg font-extrabold tracking-tight leading-none">Discrimina</div>
              <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 mt-1">Visual Learning Platform</div>
            </div>
          </div>
          <div className="mt-4"><TrialCountdownBadge /></div>
        </div>
        <nav className="px-4 flex flex-col gap-1">
          <NavLink to="/learners" className={linkClass} data-testid={NAV.learners}>
            <Users className="w-4 h-4" /> Learners
          </NavLink>
          <NavLink to="/cards" className={linkClass} data-testid={NAV.cards}>
            <LayoutGrid className="w-4 h-4" /> Card Library
          </NavLink>
          <NavLink to="/activities" className={linkClass} data-testid="nav-activities">
            <Puzzle className="w-4 h-4" /> Activities
          </NavLink>
          <NavLink to="/concepts" className={linkClass} data-testid="nav-concepts">
            <Layers className="w-4 h-4" /> Concept Groups
          </NavLink>
          <NavLink to="/billing" className={linkClass} data-testid="nav-billing">
            <CreditCard className="w-4 h-4" /> Billing
          </NavLink>
          <NavLink to="/settings" className={linkClass} data-testid="nav-settings">
            <Settings className="w-4 h-4" /> Settings
          </NavLink>
          <NavLink to="/team" className={linkClass} data-testid="nav-team">
            <UsersRound className="w-4 h-4" /> Team
          </NavLink>
          <NavLink to="/compliance" className={linkClass} data-testid="nav-compliance">
            <ShieldCheck className="w-4 h-4" /> Compliance
          </NavLink>
          {user?.is_platform_admin && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">
              <Ticket className="w-4 h-4" /> Admin
            </NavLink>
          )}
        </nav>
        <div className="mt-auto p-4">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="text-xs uppercase tracking-wider text-slate-500">Signed in</div>
            <div className="mt-1 text-sm font-semibold text-slate-800 truncate">{user?.name}</div>
            <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            <Button variant="outline" size="sm" className="w-full mt-3 rounded-full"
              onClick={handleLogout} data-testid={AUTH.logout}>
              <LogOut className="w-3.5 h-3.5 mr-2" /> Log out
            </Button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto pb-24 md:pb-0">
        <BetaTrialBanner />
        <MilestoneBanner />
        <Outlet />
      </main>

      <FeedbackWidget />
      <OnboardingTour />

      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 flex"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="mobile-bottom-nav">
        <NavLink to="/learners" className={mobileLinkClass} data-testid={NAV.learners + "-mobile"}>
          <Users className="w-5 h-5" /><span>Learners</span>
        </NavLink>
        <NavLink to="/cards" className={mobileLinkClass} data-testid={NAV.cards + "-mobile"}>
          <LayoutGrid className="w-5 h-5" /><span>Cards</span>
        </NavLink>
        <NavLink to="/activities" className={mobileLinkClass} data-testid="nav-activities-mobile">
          <Puzzle className="w-5 h-5" /><span>Play</span>
        </NavLink>
      </nav>
    </div>
  );
}
